import { useState } from "react";
import type {
  ExpressOnboardingEegColumnProps,
  ExpressOnboardingGenerator,
  ExpressOnboardingGeneratorsTableErrors,
  GetErrorFn,
  SetErrorFn
} from "../../ExpressOnboarding.types";

function useExpressOnboardingEditingState(): ExpressOnboardingEegColumnProps {
  const [errors, setErrors] = useState<ExpressOnboardingGeneratorsTableErrors>(
    {}
  );
  const [drafts, setDrafts] = useState<
    Record<
      ExpressOnboardingGenerator["id"],
      Partial<ExpressOnboardingGenerator>
    >
  >({});

  const getError: GetErrorFn = (generatorId, fieldId) => {
    return errors?.[generatorId]?.[fieldId];
  };

  const setError: SetErrorFn = (generatorId, rowErrors) => {
    setErrors((prev) => ({
      ...prev,
      [generatorId]: rowErrors
    }));
  };

  const clearError = (
    generatorId: ExpressOnboardingGenerator["id"],
    field: keyof ExpressOnboardingGenerator
  ) => {
    setErrors((prev) => {
      /* keep the same state so the table does not re-render on every keystroke */
      if (!prev[generatorId] || !(field in prev[generatorId])) {
        return prev;
      }

      const rowErrors = { ...prev[generatorId] };
      delete rowErrors[field];

      const next = { ...prev };
      if (Object.keys(rowErrors).length === 0) {
        delete next[generatorId];
      } else {
        next[generatorId] = rowErrors;
      }
      return next;
    });
  };

  const getDraft = (generatorId: ExpressOnboardingGenerator["id"]) =>
    drafts[generatorId];

  const updateDraft = (
    generatorId: ExpressOnboardingGenerator["id"],
    patch: Partial<ExpressOnboardingGenerator>
  ) => {
    setDrafts((prev) => ({
      ...prev,
      [generatorId]: { ...prev[generatorId], ...patch }
    }));
  };

  const clearDraft = (
    generatorId: ExpressOnboardingGenerator["id"],
    field: keyof ExpressOnboardingGenerator
  ) => {
    setDrafts((prev) => {
      if (!prev[generatorId] || !(field in prev[generatorId])) {
        return prev;
      }

      const rowDraft = { ...prev[generatorId] };
      delete rowDraft[field];

      const next = { ...prev };
      if (Object.keys(rowDraft).length === 0) {
        delete next[generatorId];
      } else {
        next[generatorId] = rowDraft;
      }
      return next;
    });
  };

  return {
    getError,
    setError,
    clearError,
    getDraft,
    updateDraft,
    clearDraft
  };
}

export { useExpressOnboardingEditingState };
